import React, { createContext, useContext, useState, useEffect, type ReactNode } from 'react';
import type { TenantUserRole } from '../utils/rbac';
import { getOrCreateDeviceId } from '../utils/deviceId';
import { API_ORIGIN } from '../config/api';

// Allow both frontend and backend role values
type UserRole = TenantUserRole | 'tenant_admin' | 'super_admin';

interface User {
  id: string;
  email: string;
  full_name: string;
  role: UserRole;
  email_verified?: boolean;
}

interface Tenant {
  id: string;
  name: string;
  subdomain?: string;
}

interface AuthContextType {
  user: User | null;
  tenant: Tenant | null;
  login: (email: string, password: string, tenantSubdomain?: string, tenantId?: string, turnstileToken?: string) => Promise<void>;
  requestPasswordReset: (email: string) => Promise<{ message: string }>;
  resetPassword: (token: string, password: string) => Promise<{ message: string }>;
  logout: () => void;
  isLoading: boolean;
  isAuthenticated: boolean;
  refreshEmailVerificationStatus: () => Promise<void>;
}

// Safe default — prevents crash during React error recovery renders
const authContextDefault: AuthContextType = {
  user: null,
  tenant: null,
  login: async () => {},
  requestPasswordReset: async () => ({ message: '' }),
  resetPassword: async () => ({ message: '' }),
  logout: () => {},
  isLoading: true,
  isAuthenticated: false,
  refreshEmailVerificationStatus: async () => {},
};

const AuthContext = createContext<AuthContextType>(authContextDefault);

// eslint-disable-next-line react-refresh/only-export-components
export const useAuth = () => useContext(AuthContext);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const apiBaseUrl = API_ORIGIN;

  const buildAuthUrl = (path: string) => {
    const normalizedPath = path.startsWith('/') ? path.slice(1) : path;
    return `${apiBaseUrl}/api/v1/${normalizedPath}`;
  };

  useEffect(() => {
    checkAuthStatus();
  }, []);

  const checkAuthStatus = async () => {
    try {
      const token = localStorage.getItem('access_token');
      if (token) {
        const response = await fetch(buildAuthUrl('auth/me'), {
          headers: {
            'Authorization': `Bearer ${token}`,
            'x-client-type': 'connector-service',
          },
        });

        if (response.ok) {
          const data = await response.json() as { data: { user: any; tenant: any } };
          setUser(data.data.user);
          setTenant(data.data.tenant);
          if (data.data.tenant?.id) {
            localStorage.setItem('tenant_id', data.data.tenant.id);
          }
        } else if (response.status === 401) {
          // Try to refresh the token before giving up
          const refreshToken = localStorage.getItem('refresh_token');
          if (refreshToken) {
            try {
              const refreshResponse = await fetch(buildAuthUrl('auth/refresh'), {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'x-client-type': 'connector-service',
                },
                body: JSON.stringify({ refresh_token: refreshToken }),
              });

              if (refreshResponse.ok) {
                const refreshData = await refreshResponse.json() as {
                  data?: { access_token?: string; refresh_token?: string; session_id?: string };
                };
                const newAccessToken = refreshData?.data?.access_token;
                const newRefreshToken = refreshData?.data?.refresh_token;

                if (newAccessToken && newRefreshToken) {
                  localStorage.setItem('access_token', newAccessToken);
                  localStorage.setItem('refresh_token', newRefreshToken);

                  const retryResponse = await fetch(buildAuthUrl('auth/me'), {
                    headers: {
                      'Authorization': `Bearer ${newAccessToken}`,
                      'x-client-type': 'connector-service',
                    },
                  });

                  if (retryResponse.ok) {
                    const retryData = await retryResponse.json() as { data: { user: any; tenant: any } };
                    setUser(retryData.data.user);
                    setTenant(retryData.data.tenant);
                    if (retryData.data.tenant?.id) {
                      localStorage.setItem('tenant_id', retryData.data.tenant.id);
                    }
                    return;
                  }
                }
              }
            } catch {
              // Refresh failed, fall through to clear
            }
          }

          // Clear tokens if refresh also failed
          clearAuthState();
        } else {
          clearAuthState();
        }
      }
    } catch (error) {
      console.error('Auth check failed:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (email: string, password: string, tenantSubdomain?: string, tenantId?: string, turnstileToken?: string) => {
    setIsLoading(true);
    try {
      const deviceId = await getOrCreateDeviceId();
      const storedTenantId = localStorage.getItem('tenant_id') || undefined;
      const storedTenantSubdomain = localStorage.getItem('tenant_subdomain') || undefined;
      const resolvedTenantSubdomain = tenantSubdomain || storedTenantSubdomain;
      const resolvedTenantId = tenantId || storedTenantId;

      const response = await fetch(buildAuthUrl('auth/login'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-client-type': 'connector-service',
          ...(resolvedTenantId ? { 'x-tenant-id': resolvedTenantId } : {}),
        },
        body: JSON.stringify({
          email,
          password,
          ...(resolvedTenantSubdomain ? { tenant_subdomain: resolvedTenantSubdomain } : {}),
          ...(resolvedTenantId ? { tenant_id: resolvedTenantId } : {}),
          // The API reads the challenge from this exact key (`TOKEN_FIELD` in
          // apps/api/src/lib/turnstile.ts). Sending it under any other name is
          // indistinguishable from sending nothing — the login is refused with
          // TURNSTILE_FAILED before the password is even checked.
          'cf-turnstile-response': turnstileToken || '',
          deviceId,
          deviceInfo: {
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            userAgent: navigator.userAgent,
            appVersion: 'connector-service-web',
          },
        }),
      });

      if (!response.ok) {
        const errorBody = await response.json().catch(() => ({})) as { error?: string };
        throw new Error(errorBody.error || 'Login failed');
      }

      const payload = await response.json() as {
        data?: { access_token: string; refresh_token: string; session_id?: string; user: any; tenant?: any };
        session?: { accessToken: string; refreshToken: string; user: any };
      };

      const accessToken = payload.data?.access_token || payload.session?.accessToken;
      const refreshToken = payload.data?.refresh_token || payload.session?.refreshToken;
      const sessionId = payload.data?.session_id;
      const userData = payload.data?.user || payload.session?.user;
      const tenantData = payload.data?.tenant || null;

      if (!accessToken || !refreshToken || !userData) {
        throw new Error('Login response is missing required token data');
      }

      localStorage.setItem('access_token', accessToken);
      localStorage.setItem('refresh_token', refreshToken);
      if (sessionId) {
        localStorage.setItem('session_id', sessionId);
      }
      if (tenantData?.id) {
        localStorage.setItem('tenant_id', tenantData.id);
      }
      if (tenantData?.subdomain) {
        localStorage.setItem('tenant_subdomain', tenantData.subdomain);
      }
      setUser(userData);
      setTenant(tenantData);
    } finally {
      setIsLoading(false);
    }
  };

  const requestPasswordReset = async (email: string) => {
    setIsLoading(true);
    try {
      const response = await fetch(buildAuthUrl('auth/forgot-password'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-client-type': 'connector-service',
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json().catch(() => ({})) as {
        success?: boolean;
        error?: string;
        message?: string;
      };

      if (!response.ok || data.success === false) {
        throw new Error(data.error || 'Failed to send password reset email');
      }

      return {
        message: data.message || 'Password reset email sent',
      };
    } finally {
      setIsLoading(false);
    }
  };

  const resetPassword = async (token: string, password: string) => {
    setIsLoading(true);
    try {
      const response = await fetch(buildAuthUrl('auth/reset-password'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-client-type': 'connector-service',
        },
        body: JSON.stringify({ token, password }),
      });

      const data = await response.json().catch(() => ({})) as {
        success?: boolean;
        error?: string;
        message?: string;
      };

      if (!response.ok || data.success === false) {
        throw new Error(data.error || 'Failed to reset password');
      }

      return {
        message: data.message || 'Password updated successfully',
      };
    } finally {
      setIsLoading(false);
    }
  };

  const clearAuthState = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('session_id');
    localStorage.removeItem('tenant_id');
    localStorage.removeItem('tenant_subdomain');
  };

  const logout = () => {
    clearAuthState();
    setUser(null);
    setTenant(null);
  };

  const refreshEmailVerificationStatus = async () => {
    try {
      const token = localStorage.getItem('access_token');
      if (!token) return;

      const response = await fetch(buildAuthUrl('auth/me'), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'x-client-type': 'connector-service',
        },
      });

      if (response.ok) {
        const data = await response.json() as { data: { user: any } };
        setUser(prev => prev ? { ...prev, email_verified: data.data.user.email_verified } : null);
      }
    } catch (error) {
      console.error('Failed to refresh email verification status:', error);
    }
  };

  const value: AuthContextType = {
    user,
    tenant,
    login,
    requestPasswordReset,
    resetPassword,
    logout,
    isLoading,
    isAuthenticated: !!user,
    refreshEmailVerificationStatus,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
